import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import _get from 'lodash/get';
import Typography from '@lib/components/Typography/Typography';
import {
  MAX_TEXT_EDITOR_FIELD_LENGTH,
  MAX_TEXT_FIELD_LENGTH,
  Steps,
} from '@lib/enums/form';
import Input from '@lib/components/Input/Input';
import Select, { SelectProps } from '@lib/components/Select/Select';
import { getFieldValidation, getOptionShape } from '@lib/utils/validationUtils';
import { getTaskPriorityOptions } from '@lib/utils/priority';
import styles from '@lib/assets/styles/Form.module.scss';
import { GetLoadOptions } from '@lib/components/Select/types';
import { FieldNames } from 'routes/Task/enums';
import VersionOption from 'components/VersionOption/VersionOption';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import ChipOption from '@lib/components/Select/components/ChipOption';
import ChipValueContainer from '@lib/components/Select/components/ChipValueContainer';
import getCreateTaskScopeFields from './getCreateTaskScopeFields';
import getEditTaskScopeFields from './getEditTaskScopeFields';
import getDisabledTaskScopeFields from './getDisabledTaskScopeFields';

type TaskFieldsArgs = {
  canQrCodeRelatedInfoUpdate?: boolean;
  getApproversLoadOptions: GetLoadOptions;
  getAssetsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getTemplateVersionsLoadOptions?: GetLoadOptions;
  getTemplatesLoadOptions?: GetLoadOptions;
  openAreasModalTableMultiselect?: () => void;
  openAssetsModalTableMultiselect?: () => void;
  openAreasModalTableMultiunselect?: () => void;
  openAssetsModalTableMultiunselect?: () => void;
  isCreateForm?: boolean;
  onCreateNewFormTemplate?: () => void;
  onViewFormTemplate?: (
    templateFormId: string,
    templateFormVersionId: string,
  ) => void;
  step?: Steps;
  t: TFunction<'translation', undefined>;
  isCreate: boolean;
};

export default function getTaskFields(args: TaskFieldsArgs): FieldItem[] {
  const {
    canQrCodeRelatedInfoUpdate = true,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
    step = Steps.step1,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    t,
    isCreate,
  } = args;
  return [
    {
      name: FieldNames.taskDetails,
      customRender: () => (
        <Typography variant="h3">{t('task-details')}</Typography>
      ),
      step,
    },
    {
      name: FieldNames.taskName,
      label: t('task-name'),
      element: Input,
      validation: Yup.string()
        .trim()
        .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
        .required(t('required-field-error')),
      step,
    },
    {
      name: FieldNames.taskDescription,
      label: t('task-description'),
      element: TextEditor,
      validation: getFieldValidation({
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
      step,
    },
    {
      name: FieldNames.priority,
      label: t('task-priority'),
      element: Select,
      validation: Yup.object()
        .shape(getOptionShape(t, true))
        .required(t('required-field-error')),
      componentProps: {
        options: getTaskPriorityOptions(),
        isSearchable: false,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
      },
      step,
    },
    {
      name: FieldNames.templateTitle,
      customRender: () => (
        <Typography variant="h3" className={styles.blockStart}>
          {t('attach-form-template')}
        </Typography>
      ),
      step,
      isVisibleFn: () =>
        !!getTemplatesLoadOptions && !!getTemplateVersionsLoadOptions,
    },
    {
      name: FieldNames.template,
      label: t('form-template'),
      element: Select,
      validation: Yup.object()
        .shape(getOptionShape(t, true))
        .required(t('required-field-error')),
      componentProps: {
        disabled: !canQrCodeRelatedInfoUpdate,
        disabledTooltipProps: canQrCodeRelatedInfoUpdate
          ? undefined
          : {
              body: t('disabled-field-desc-for-task-form-template'),
            },
        isAsync: true,
        isClearable: false,
        getLoadOptions: getTemplatesLoadOptions,
        defaultOptions: false,
        createNewOptionProps: {
          title: t('create-new'),
          onCLick: onCreateNewFormTemplate,
        },
      } as SelectProps,
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === FieldNames.template) {
          setValue(FieldNames.templateVersion, null, { shouldValidate: false });
        }
      },
      step,
      formItemClassName: styles.formColumn9,
      useKeyRefreshOnWindowFocus: true,
      isVisibleFn: () =>
        !!getTemplatesLoadOptions && !!getTemplateVersionsLoadOptions,
    },
    {
      name: FieldNames.templateVersion,
      label: t('version'),
      element: Select,
      validation: Yup.object()
        .shape(getOptionShape(t, true))
        .required(t('required-field-error')),
      componentProps: {
        isAsync: true,
        isClearable: false,
        getLoadOptions: getTemplateVersionsLoadOptions,
        defaultOptions: false,
        components: {
          Option: VersionOption,
        },
        disabledTooltipProps: {
          body: t('choose-form-template-first'),
        },
      } as SelectProps,
      isDisabledFn: (values: Values, name?: string) => {
        if (!name) return false;
        const depsName = name.replace(
          FieldNames.templateVersion,
          `${FieldNames.template}.value`,
        );
        const depsValue = _get(values, depsName);
        return depsValue === undefined;
      },
      step,
      formItemClassName: styles.formColumn3,
      getFieldKeyFn: (values: Values, name?: string) => {
        const templateId = _get(values, `${FieldNames.template}.value`);
        if (templateId) {
          return `${name}-${FieldNames.templateVersion}-${templateId}`;
        }
        return FieldNames.templateVersion;
      },
      useKeyRefreshOnWindowFocus: true,
      isVisibleFn: () =>
        !!getTemplatesLoadOptions && !!getTemplateVersionsLoadOptions,
    },
    {
      name: FieldNames.templateVersionViewBtn,
      customRender: (values: Values) =>
        onViewFormTemplate ? (
          <Button
            buttonText={t('view-template')}
            buttonType={ButtonTypes.primaryOutlined}
            buttonSize={ButtonSizes.small}
            rightIcon="arrow_outward"
            onClick={() =>
              onViewFormTemplate(
                _get(values, [FieldNames.template, 'value']),
                _get(values, [FieldNames.templateVersion, 'value']),
              )
            }
          />
        ) : (
          <div />
        ),
      isVisibleFn: (values: Values) =>
        onViewFormTemplate &&
        _get(values, [FieldNames.template, 'value']) &&
        _get(values, [FieldNames.templateVersion, 'value']) &&
        !!getTemplatesLoadOptions &&
        !!getTemplateVersionsLoadOptions,
      step,
    },
    ...(isCreate ? getCreateTaskScopeFields(args) : []),
    ...(!isCreate && canQrCodeRelatedInfoUpdate
      ? getEditTaskScopeFields(args)
      : []),
    ...(!isCreate && !canQrCodeRelatedInfoUpdate
      ? getDisabledTaskScopeFields(args)
      : []),
  ];
}
